// @flow

import React, { useState } from 'react';
import type { Node } from 'react';
import classNames from 'classnames';
import { Button } from '@riseart/common';
import { CloseIcon } from '@riseart/icons';
import { product as PRODUCT_ENUM } from 'Enum';

import {
  urgentMessageCls,
  urgentMessageHighlightCls,
  urgentMessageContentCls,
  urgentMessageCloseCls,
} from 'shared_components/arts/containers/messages/Urgent.less';

type Props = {
  className?: string,
  onClose?: Function,
  heading: Node,
  children: Node,
  closable?: boolean,
};

/**
 * getUrgentMessage
 *
 * @param {Object} param0
 * @param {Function} formatMessage
 * @returns { heading: String, content: string } | null
 */
export function getUrgentMessage(
  { productCanBuy, productType, productPrimarySku }: Object,
  formatMessage: Function,
): { heading: String, content: string } | null {
  const isOriginal = productType === PRODUCT_ENUM.type.TYPE_ORIGINAL;
  const stock = productPrimarySku && productPrimarySku.stock;

  if (!productCanBuy) {
    return null;
  }

  if (isOriginal) {
    return {
      heading: formatMessage({ id: 'components.art.urgentMessages.original.heading' }),
      content: formatMessage({ id: 'components.art.urgentMessages.original.content' }),
    };
  }

  if (stock > 3) {
    return {
      heading: formatMessage({ id: 'components.art.urgentMessages.edition.heading' }),
      content: formatMessage({ id: 'components.art.urgentMessages.edition.content' }),
    };
  }

  return {
    heading: formatMessage({ id: 'components.art.urgentMessages.editionLimited.heading' }),
    content: formatMessage(
      {
        id: 'components.art.urgentMessages.editionLimited.content',
      },
      { stock },
    ),
  };
}

/**
 * UrgentMessage
 *
 * @param {Props} props
 * @returns {Node}
 */
export const UrgentMessage = ({
  className = null,
  onClose,
  heading,
  children,
  closable = true,
}: Props): Node => {
  const [isClosed, setIsClosed] = useState(false);

  const handleCloseClick =
    closable &&
    (() => {
      setIsClosed(true);
      if (typeof onClose === 'function') {
        onClose();
      }
    });

  if (closable && isClosed) {
    return null;
  }

  return (
    <div className={classNames(urgentMessageCls, className)}>
      <strong className={urgentMessageHighlightCls}>{heading}</strong>
      <span className={urgentMessageContentCls}>{children}</span>
      {closable ? (
        <Button type="link" onClick={handleCloseClick} className={urgentMessageCloseCls}>
          <CloseIcon />
        </Button>
      ) : null}
    </div>
  );
};
